// Mini modal with picture separated by a vertical line
import React from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import { type RestSpeechBubble2Modal } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { useCardRenderEnvironment } from '@bbkFunnelComponents/card-render-environment';
import {
  Avatar,
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestSpeechBubble2Modal;
  close: () => void;
  detailedAction: React.ReactElement;
};

const Details = styled.div`
  margin-bottom: 1.5rem;
  overflow-y: auto;
  max-height: 250px;
  border-radius: 10px;
  padding: 1.5rem 1rem;
`;

const RenderSpeechBubbleV2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      info: { title, details },
      person,
    },
    mobileLayout,
  },
  close,
  detailedAction,
}) => {
  const { mobileView } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;
  const backgroundIsDark = new TinyColor(theme.modalBackground).isDark();
  const backgroundModalColor = new TinyColor(theme.modalBackground);
  const backgroundColor = backgroundIsDark
    ? backgroundModalColor.lighten(7).toHexString()
    : backgroundModalColor.darken(4).toHexString();

  const { previewCanvas } = useCardRenderEnvironment();

  const mobileViewContent = (
    <ModalMobileInnerSpacer
      css={{
        gridTemplateRows: 'auto minmax(1rem, auto) 1fr auto',
      }}
      className={cs('display-grid', 'mobile', 'speech-bubble')}
    >
      <OfferTitleMobile
        title={<TitleTheme data-part="title" title={title} />}
        close={close}
      />
      <Details
        style={{ backgroundColor }}
        css={css`
          margin-bottom: 0px;
          max-height: unset;
        `}
        data-part="description"
      >
        <CustomerHtml value={details} />
      </Details>
      <div
        css={css`
          padding-top: 1rem;
          padding-bottom: 1rem;
        `}
        data-part="image"
      >
        <Avatar
          data-part="person"
          person={person}
          includeArrow
          arrowStyles={css`
            top: calc(-5px - 1rem);
            left: 47.5px;
            transform: rotate(-90deg);
          `}
          arrowColor={backgroundColor}
        />
      </div>
      <div>{detailedAction}</div>
    </ModalMobileInnerSpacer>
  );

  if (previewCanvas?.device === 'mobile') {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {mobileViewContent}
      </MobileDevicePreview>
    );
  }

  if (mobileView) return mobileViewContent;

  return (
    <ModalDesktopInnerSpacer
      css={css`
        max-width: 650px;
      `}
      className={cs('speech-bubble', 'display-grid', 'desktop', 'inner-spacer')}
    >
      <OfferTitleMobile
        title={<TitleTheme data-part="title" title={title} />}
        close={close}
      />
      <div
        css={css`
          margin-top: 1rem;
        `}
        className={cs('display-grid', 'content-grid', 'align-items--center')}
      >
        <div data-part="image">
          <div>
            <Avatar
              data-part="person"
              person={person}
              vertical
              alignRight
              includeArrow
              arrowColor={backgroundColor}
            />
          </div>
        </div>
        <div className={cs('display-grid', 'content-items')}>
          <Details style={{ backgroundColor }} data-part="description">
            <CustomerHtml value={details} />
          </Details>
          <div>{detailedAction}</div>
        </div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};

const SpeechBubbleV2 = Object.assign(RenderSpeechBubbleV2, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    person: {
      path: ['requires', 'person'],
      control: 'group',
      selector: '[data-part="person"]',
      placement: 'bottom',
      fields: [
        {
          path: ['name'],
          control: 'text',
          label: 'Name',
        },
        {
          path: ['title'],
          control: 'text',
          label: 'Title',
        },
        {
          path: ['preposition'],
          control: 'text',
          label: 'Preposition',
        },
        {
          path: ['organization'],
          control: 'text',
          label: 'Company',
        },
      ],
    },
    image: {
      path: ['requires', 'person', 'avatar'],
      control: 'image',
      selector: '[data-part="image"]',
      placement: 'top-end',
      options: {
        helpLabel: 'Recommended size: 250x250px; < 100kB',
      },
    },
    primaryAction: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
export { SpeechBubbleV2 };
